import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Box, Tabs, Tab, Grid, Badge, Typography } from '@mui/material';

import AnalyzerSnapshotsSettings from '../snapshots/SnapshotsSettings';
import MarkerConfigurationDialog from '../markerConfiguration/MarkerConfigurationDialog';
import CrossPolDialog from '../crosspol/CrossPolDialog';

import { useIsSnapshotLocked } from '../analyzers/hooks/useIsSnapshotLocked';
import { useIsTimeshareEnabled } from 'features/analyzerModules/api/getAnalyzerModules';

import { getSnapshotValidation } from 'api/snapshotValidationApi';
import CurrentConditions from 'features/weather/CurrentConditions';
import { useIsWeatherAlerting } from 'features/weatherAlerts/hooks/useIsWeatherAlerting';
import { grey } from '@mui/material/colors';
import {
  Snapshot,
  TraceHistory,
  WeatherConstraint,
  SnapshotValidation,
} from 'types';
import { useGetHistoricalWeatherConditions } from 'features/weatherReflection/hooks/useGetHistoricalWeatherConditions';
import { useAPICall } from 'hooks/useAPICall';
import { useLocationByAnalyzer } from 'features/locations/api/getLocationByAnalyzer';
import { useSystemModules } from 'features/systemModules/api/getSystemModules';
import TimeShare from 'features/timeShare/TimeShare';
import SnapshotDetailsPanel from '../snapshotDetails/SnapshotDetailsPanel';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={1}>{children}</Box>}
    </div>
  );
};

interface Props {
  selectedAnalyzerId: number;
  selectedSnapshotId: number;
  setSelectedSnapshotId?: Dispatch<SetStateAction<number>>;
  selectedSnapshot: Snapshot | TraceHistory;
  editSnapshotsDisabled: boolean;
  editMarkersDisabled: boolean;
  displayActiveSnapshotSelector: boolean;
  isHistoricalMode?: boolean;
  inDrawingMode?: boolean;
  inDialog?: boolean;
  traceMode?: number;
  currentTimestamp?: string;
  weatherConstraints?: WeatherConstraint[];
}

const SettingsTabPanel = ({
  selectedAnalyzerId,
  selectedSnapshotId,
  setSelectedSnapshotId,
  selectedSnapshot,
  editSnapshotsDisabled,
  editMarkersDisabled,
  displayActiveSnapshotSelector,
  isHistoricalMode,
  inDrawingMode,
  inDialog,
  traceMode,
  currentTimestamp,
  weatherConstraints,
}: Props) => {
  const isSnapshotLocked = useIsSnapshotLocked(selectedSnapshot);
  const isTimeshareEnabled = useIsTimeshareEnabled(selectedAnalyzerId);
  const { data: modules } = useSystemModules({});
  const [value, setValue] = useState(0);
  const [snapshotValidation, setSnapshotValidation] = useState<
    SnapshotValidation | undefined | null
  >(null);
  const [markersHaveChanges, setMarkersHaveChanges] = useState(false);

  const { data: locationForAnalyzer } = useLocationByAnalyzer({
    analyzerId: selectedAnalyzerId,
  });
  const weatherAlerts = useIsWeatherAlerting(selectedAnalyzerId);

  const historicalWeatherConditions = useGetHistoricalWeatherConditions(
    locationForAnalyzer?.id,
    currentTimestamp,
    !isHistoricalMode
  );

  const { safeCall: safeGetSnapshotValidation } = useAPICall(
    getSnapshotValidation
  );

  const fetchValidation = useCallback(async () => {
    const response = await safeGetSnapshotValidation(selectedSnapshotId);
    setSnapshotValidation(response);
  }, [selectedSnapshotId, safeGetSnapshotValidation]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleCloseMarkers = () => {
    setValue(0);
  };

  useEffect(() => {
    // TS doesn't like that crosspol_snapshot_id might not be a property of
    //    selectedSnapshot (as in trace history), but that's the point of the if statement,
    //    and we don't want this code to run every time the snapshot/trace changes in trace history
    // @ts-ignore
    if (!selectedSnapshot.crosspol_snapshot_id) setValue(0);
    else setValue(2);
    // @ts-ignore
  }, [selectedSnapshot?.crosspol_snapshot_id]);

  useEffect(() => {
    fetchValidation();
  }, [fetchValidation]);

  return (
    <Box sx={{ width: '100%' }}>
      <Grid container justifyContent='space-between' alignItems='center'>
        <Grid item>
          <Tabs
            // Prevent errors on accessing the crosspol tab when crosspol isn't active
            value={
              Number(value) === 2
                ? 'crosspol_snapshot_id' in selectedSnapshot &&
                  !!selectedSnapshot.crosspol_snapshot_id
                  ? value
                  : 1
                : value
            }
            onChange={handleChange}
            aria-label='secondary tabs example'
          >
            <Tab value={0} label='Settings' />
            {!editMarkersDisabled && (
              <Tab
                value={1}
                label='Markers'
                disabled={
                  ('crosspol_snapshot_id' in selectedSnapshot &&
                    !!selectedSnapshot.crosspol_snapshot_id) ||
                  traceMode !== 0
                }
              />
            )}
            {'crosspol_snapshot_id' in selectedSnapshot &&
              !!selectedSnapshot.crosspol_snapshot_id && (
                <Tab
                  value={2}
                  label='Cross-Pol'
                  disabled={!selectedSnapshot.crosspol_snapshot_id}
                />
              )}
            {modules?.WEATHER && (
              <Tab
                value={3}
                label={
                  <Badge
                    variant='dot'
                    color={weatherAlerts.warning ? 'error' : 'warning'}
                    invisible={
                      isHistoricalMode ||
                      !!historicalWeatherConditions ||
                      (!weatherAlerts.warning && !weatherAlerts.caution)
                    }
                  >
                    <span style={{ marginRight: '.3rem' }}>Weather</span>
                  </Badge>
                }
              />
            )}
            {isTimeshareEnabled && !isHistoricalMode && (
              <Tab value={4} label='Timeshare' />
            )}
            <Tab value={5} label='Details' />
          </Tabs>
        </Grid>
      </Grid>
      <TabPanel value={value} index={0}>
        <AnalyzerSnapshotsSettings
          selectedAnalyzerId={selectedAnalyzerId}
          selectedSnapshot={selectedSnapshot}
          setSelectedSnapshotId={setSelectedSnapshotId}
          displayActiveSnapshotSelector={displayActiveSnapshotSelector}
          editDisabled={editSnapshotsDisabled || isSnapshotLocked}
          inDialog={inDialog}
          inHistoryMode={isHistoricalMode}
          validatedSnapshotProperties={snapshotValidation}
          setValidatedSnapshotProperties={setSnapshotValidation}
        />
      </TabPanel>
      {!editMarkersDisabled && (
        <TabPanel value={value} index={1}>
          <MarkerConfigurationDialog
            handleClose={handleCloseMarkers}
            selectedAnalyzerId={selectedAnalyzerId}
            selectedSnapshotId={selectedSnapshotId}
            historicalSnapshot={selectedSnapshot}
            isHistoricalMode={isHistoricalMode}
            inDrawingMode={inDrawingMode}
            hasChanges={markersHaveChanges}
            setHasChanges={setMarkersHaveChanges}
          />
        </TabPanel>
      )}
      {'crosspol_snapshot_id' in selectedSnapshot &&
        selectedSnapshot.crosspol_snapshot_id && (
          <TabPanel value={value} index={2}>
            <CrossPolDialog
              selectedAnalyzerId={selectedAnalyzerId}
              selectedSnapshotId={selectedSnapshot.id}
              crossPolSnapshotId={selectedSnapshot.crosspol_snapshot_id}
            />
          </TabPanel>
        )}
      {modules?.WEATHER && (
        <TabPanel value={value} index={3}>
          {locationForAnalyzer ? (
            <CurrentConditions
              currentConditions={
                isHistoricalMode
                  ? historicalWeatherConditions
                  : locationForAnalyzer.weather &&
                    locationForAnalyzer.weather.length > 0
                  ? locationForAnalyzer.weather[0]
                  : undefined
              }
              location={locationForAnalyzer}
              inTraceHistory={isHistoricalMode}
              analyzerId={selectedAnalyzerId}
              weatherConstraints={weatherConstraints}
            />
          ) : (
            <Typography color={grey[500]}>
              No location associated with this analyzer. To see weather
              conditions, pair the analyzer or its switch with a dish with an
              enabled weather location.
            </Typography>
          )}
        </TabPanel>
      )}
      {isTimeshareEnabled && (
        <TabPanel value={value} index={4}>
          <TimeShare
            analyzerId={selectedAnalyzerId}
            snapshotId={selectedSnapshotId}
          />
        </TabPanel>
      )}
      <TabPanel value={value} index={5}>
        <SnapshotDetailsPanel selectedSnapshotId={selectedSnapshotId} />
      </TabPanel>
    </Box>
  );
};

export default SettingsTabPanel;
